import type { BottomNavigationConfig } from '@xingternal/platform-layout-bottom-bar';

import type { ConfigurationItem, Translate, Language } from '../types';
import { XING_3_FLAG } from './shared';

const NAVIGATION_BADGE_NO_BADGE = 'navigation_badge_no_badge';

const moreMenu: ConfigurationItem = (t): BottomNavigationConfig => [
  [
    'FeatureSwitch',
    {
      flags: ['armstrong_marketing_entry_point_menu'],
      children: {
        $meta: [
          [
            'Group',
            {
              key: 'divider-1',
              children: {
                $meta: [
                  ['FeedbackAndHelp', { 'data-testid': 'feedback-and-help' }],
                ],
              },
            },
          ],
        ],
      },
      elseCase: {
        $meta: [
          [
            'UpsellHeadline',
            {
              'data-testid': 'upsell-headline',
              size: 'small',
              children: t('FRAME_NAVIGATION_UPSELL_HEADLINE'),
            },
          ],
        ],
      },
    },
  ],

  [
    'GroupedList',
    {
      key: 'upsellItems',
      children: {
        $meta: [
          [
            'FeatureCondition',
            {
              flags: ['isPremium'],
              children: {
                $meta: [
                  [
                    'MoreMenuItem',
                    {
                      item: {
                        key: 'premium',
                        icon: { $component: 'IconPremium' },
                        activeIcon: {
                          $component: 'IconPremiumFilled',
                        },
                        title: t('FRAME_NAVIGATION_YOUR_PREMIUM'),
                        href: '/premium',
                      },
                    },
                  ],
                ],
              },
              elseCase: {
                $meta: [
                  [
                    'MoreMenuItem',
                    {
                      item: {
                        key: 'premiumUpsellArmstrong',
                        icon: { $component: 'IconPremium' },
                        activeIcon: {
                          $component: 'IconPremiumFilled',
                        },
                        title: t(
                          'VERTICAL_NAVIGATION_PREMIUM_BENEFITS_FOR_BASIC_USER'
                        ),
                        href: '/premium',
                      },
                    },
                  ],
                ],
              },
            },
          ],

          [
            'FeatureCondition',
            {
              flags: ['hasProJobsMembership'],
              children: {
                $meta: [
                  [
                    'MoreMenuItem',
                    {
                      item: {
                        key: 'proJobs',
                        icon: { $component: 'IconProJobs' },
                        activeIcon: {
                          $component: 'IconProJobsFilled',
                        },
                        href: '/projobs',
                        title: t('FRAME_NAVIGATION_YOUR_PROJOBS'),
                      },
                    },
                  ],
                ],
              },
              elseCase: {
                $meta: [
                  [
                    'MoreMenuItem',
                    {
                      item: {
                        key: 'proJobsUpsellArmstrong',
                        icon: { $component: 'IconProJobs' },
                        activeIcon: {
                          $component: 'IconProJobsFilled',
                        },
                        href: '/projobs',
                        title: t('FRAME_NAVIGATION_PROJOBS_UPSELL'),
                      },
                    },
                  ],
                ],
              },
            },
          ],
        ],
      },
    },
  ],
  [
    'GroupedList',
    {
      key: 'navigationItems',
      children: {
        $meta: [
          [
            'FeatureCondition',
            {
              flags: ['showProbusinessInNavigation'],
              children: {
                $meta: [
                  [
                    'MoreMenuItem',
                    {
                      item: {
                        key: 'proBusinessArmstrong',
                        icon: { $component: 'IconProBusiness' },
                        activeIcon: {
                          $component: 'IconProBusinessFilled',
                        },
                        title: t('VERTICAL_NAVIGATION_PROBUSINESS'),
                        href: '/probusiness',
                        premium: true,
                        tracking: {
                          sc_o: 'navigation_probusiness',
                          sc_o_PropActionOrigin: NAVIGATION_BADGE_NO_BADGE,
                        },
                      },
                    },
                  ],
                ],
              },
            },
          ],
          [
            'MoreMenuItem',
            {
              item: {
                key: 'contentArmstrong',
                icon: { $component: 'IconNews' },
                activeIcon: { $component: 'IconNewsFilled' },
                title: t('VERTICAL_NAVIGATION_CONTENT_PAGES'),
                href: '/news',
                tracking: {
                  sc_o: 'navigation_news',
                  sc_o_PropActionOrigin: NAVIGATION_BADGE_NO_BADGE,
                },
              },
            },
          ],
          [
            'MoreMenuItem',
            {
              item: {
                key: 'companies',
                icon: { $component: 'IconCompanies' },
                activeIcon: { $component: 'IconCompaniesFilled' },
                title: t('VERTICAL_NAVIGATION_COMPANIES'),
                href: '/companies',
                tracking: {
                  sc_o: 'navigation_companies',
                  sc_o_PropActionOrigin: NAVIGATION_BADGE_NO_BADGE,
                },
              },
            },
          ],
          [
            'MoreMenuItem',
            {
              item: {
                key: 'business',
                icon: { $component: 'IconBusinessSolutions' },
                activeIcon: {
                  $component: 'IconBusinessSolutionsFilled',
                },
                title: t('VERTICAL_NAVIGATION_BUSINESS_SOLUTIONS'),
                href: '/business-solutions',
                tracking: {
                  sc_o: 'navigation_business_solutions',
                  sc_o_PropActionOrigin: NAVIGATION_BADGE_NO_BADGE,
                },
              },
            },
          ],
        ],
      },
    },
  ],
  [
    'ExtraListItems',
    {
      key: 'extraItems',
      children: {
        $meta: [
          [
            'MoreMenuItem',
            {
              item: {
                key: 'settingsPersonal',
                icon: { $component: 'IconSettings' },
                href: '/settings',
                title: t('VERTICAL_NAVIGATION_HEADER_PERSONAL_SETTINGS'),
                tracking: {
                  sc_o: 'navigation_settings',
                  sc_o_PropActionOrigin: NAVIGATION_BADGE_NO_BADGE,
                },
              },
            },
          ],
          [
            'FeatureSwitch',
            {
              flags: ['armstrong_v2_logout_in_settings'],
              children: [],
              elseCase: {
                $meta: [
                  [
                    'LogOutButton',
                    {
                      item: {
                        key: 'logoutPersonal',
                        icon: { $component: 'IconLogout' },
                        href: '/login/logout',
                        title: t('VERTICAL_NAVIGATION_HEADER_PERSONAL_LOGOUT'),
                      },
                    },
                  ],
                ],
              },
            },
          ],
        ],
      },
    },
  ],
];

export const bottomNavigation: ConfigurationItem = (
  t: Translate,
  lang: Language
): BottomNavigationConfig => {
  return [
    [
      'Fragment',
      {
        children: { $meta: bottomNavigationOld(t, lang) },
        elseCase: { $meta: bottomNavigationXing3(t, lang) },
        flag: XING_3_FLAG,
        minVersion: 2,
      },
    ],
  ];
};

export const bottomNavigationOld: ConfigurationItem = (
  t: Translate,
  lang: Language
): BottomNavigationConfig => [
  [
    'MainList',
    {
      // @ts-expect-error Not a checked prop, just passed through:
      'data-testid': 'MainList',
      children: {
        $meta: [
          [
            'MainListItem',
            {
              item: {
                key: 'disco',
                icon: { $component: 'IconHome' },
                activeIcon: { $component: 'IconHomeFilled' },
                title: t('FRAME_NAVIGATION_SUPI'),
                href: '/discover/updates',
                tracking: {
                  sc_o: 'navigation_discover',
                  sc_o_PropActionOrigin: [
                    'navigation_badge',
                    NAVIGATION_BADGE_NO_BADGE,
                  ],
                },
              },
            },
          ],
          [
            'MainListItem',
            {
              item: {
                key: 'jobs',
                icon: { $component: 'IconJobs' },
                activeIcon: { $component: 'IconJobsFilled' },
                title: t('FRAME_NAVIGATION_JOBS'),
                href: '/jobs',
                notificationKey: 'jobs',
                tracking: {
                  sc_o: 'navigation_jobs',
                  sc_o_PropActionOrigin: [
                    'navigation_badge_jobs',
                    NAVIGATION_BADGE_NO_BADGE,
                  ],
                },
              },
            },
          ],
          [
            'MainListItem',
            {
              item: {
                key: 'me-hub',
                icon: { $component: 'IconContact' },
                activeIcon: { $component: 'IconContactFilled' },
                title: t('FRAME_NAVIGATION_ME_HUB'),
                href: '/you',
                tracking: {
                  sc_o: 'navigation_me',
                  sc_o_PropActionOrigin: [
                    'navigation_badge_me',
                    NAVIGATION_BADGE_NO_BADGE,
                  ],
                },
              },
            },
          ],
          [
            'MainListItem',
            {
              item: {
                key: 'supi',
                icon: {
                  $component: 'IconNotificationCenter',
                },
                activeIcon: {
                  $component: 'IconNotificationFilled',
                },
                title: t('NOTIFICATION_CENTER_LINK'),
                href: '/notifications',
                notificationKey: 'networkHome',
                tracking: {
                  sc_o: 'navigation_exchange',
                  sc_o_PropActionOrigin: [
                    'navigation_badge_exchange',
                    NAVIGATION_BADGE_NO_BADGE,
                  ],
                },
              },
            },
          ],
          [
            'MoreMenu',
            {
              item: {
                key: 'more',
                role: 'button',
                icon: { $component: 'IconMore' },
                title: t('VERTICAL_NAVIGATION_MORE'),
              },
              children: {
                $meta: moreMenu(t, lang),
              },
            },
          ],
        ],
      },
    },
  ],
];

export const bottomNavigationXing3: ConfigurationItem = (
  t: Translate,
  _lang: Language
): BottomNavigationConfig => [
  [
    'MainList',
    {
      // @ts-expect-error Not a checked prop, just passed through:
      'data-testid': 'MainList',
      children: {
        $meta: [
          [
            'MainListItem',
            {
              item: {
                key: 'jobs',
                icon: { $component: 'IconJobs' },
                activeIcon: { $component: 'IconJobsFilled' },
                title: t('BOTTOM_BAR_FIND_JOBS_LABEL'),
                href: '/jobs/find',
                tracking: {
                  sc_o: 'navigation_find_jobs',
                  sc_o_PropActionOrigin: [
                    'navigation_badge_find_jobs',
                    NAVIGATION_BADGE_NO_BADGE,
                  ],
                },
              },
            },
          ],
          [
            'MainListItem',
            {
              item: {
                key: 'my-jobs',
                icon: { $component: 'IconBookmark' },
                activeIcon: { $component: 'IconBookmarkFilled' },
                title: t('BOTTOM_BAR_YOUR_JOBS_LABEL'),
                href: '/jobs/my-jobs',
                tracking: {
                  sc_o: 'navigation_your_jobs',
                  sc_o_PropActionOrigin: [
                    'navigation_badge_your_jobs',
                    NAVIGATION_BADGE_NO_BADGE,
                  ],
                },
              },
            },
          ],
          [
            'MainListItem',
            {
              item: {
                key: 'contacts',
                icon: { $component: 'IconContacts' },
                activeIcon: { $component: 'IconContactsFilled' },
                title: t('BOTTOM_BAR_NETWORK_LABEL'),
                href: '/network',
                tracking: {
                  sc_o: 'navigation_network',
                  sc_o_PropActionOrigin: [
                    'navigation_badge_network',
                    NAVIGATION_BADGE_NO_BADGE,
                  ],
                },
              },
            },
          ],
          [
            'MainListItem',
            {
              item: {
                key: 'disco',
                icon: { $component: 'IconNews' },
                activeIcon: { $component: 'IconNewsFilled' },
                title: t('BOTTOM_BAR_INSIGHTS_LABEL'),
                href: '/discover/updates',
                notificationKey: 'insights',
                indeterminate: true,
                tracking: {
                  sc_o: 'navigation_insights',
                  sc_o_PropActionOrigin: NAVIGATION_BADGE_NO_BADGE,
                },
              },
            },
          ],
          [
            'MainListItem',
            {
              item: {
                key: 'supi',
                icon: {
                  $component: 'IconNotificationCenter',
                },
                activeIcon: {
                  $component: 'IconNotificationFilled',
                },
                title: t('BOTTOM_BAR_NOTIFICATIONS_LABEL'),
                href: '/notifications',
                notificationKey: 'notificationCenterWebVisionType',
                tracking: {
                  sc_o: 'navigation_notifications',
                  sc_o_PropActionOrigin: [
                    'navigation_badge_notifications',
                    NAVIGATION_BADGE_NO_BADGE,
                  ],
                },
              },
            },
          ],
        ],
      },
    },
  ],
];
